import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import logger from '../utils/logger';
import {
  fetchUserPromotions,
  fetchPromotionHistory,
  addPromotion,
  updatePromotion,
  deletePromotion,
  clearPromotions,
  resetError,
  BeltPromotion,
  selectPromotions,
  selectPromotionIds,
  selectCurrentBelts,
  selectBeltProgressionLoading,
  selectBeltProgressionError
} from '../store/slices/beltProgressionSlice';

/**
 * Custom hook for accessing and manipulating belt progression data
 */
export const useBeltProgression = () => {
  const dispatch = useAppDispatch();
  
  // Select state from Redux store
  const promotions = useAppSelector(selectPromotions);
  const promotionIds = useAppSelector(selectPromotionIds);
  const currentBelts = useAppSelector(selectCurrentBelts);
  const isLoading = useAppSelector(selectBeltProgressionLoading);
  const error = useAppSelector(selectBeltProgressionError);
  
  // Convert promotions object to sorted array (newest first)
  const promotionsArray = promotionIds
    .map(id => promotions[id])
    .sort((a, b) => b.date - a.date);
  
  // Get current belt for a specific martial art
  const getCurrentBelt = useCallback((martialArt: string) => {
    return currentBelts[martialArt] || null;
  }, [currentBelts]);
  
  // Load a user's promotions
  const loadUserPromotions = useCallback(async (userId: string) => {
    try {
      const promotions = await dispatch(fetchUserPromotions(userId)).unwrap();
      
      // Also load promotion history
      await dispatch(fetchPromotionHistory(userId)).unwrap();
      
      return promotions;
    } catch (error) {
      logger.error('[useBeltProgression] Error loading promotions:', error);
      throw error;
    }
  }, [dispatch]);

  // Get promotion history for a user - this will be handled directly in components
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _getPromotionHistory = useCallback((userId?: string) => {
    // This function should not be used in useEffect dependencies
    // Components should use the selector directly instead
    logger.warn('getPromotionHistory should not be used in useEffect dependencies');
    return [];
  }, []);
  
  // Add a new promotion
  const createPromotion = useCallback((promotionData: Omit<BeltPromotion, 'id'>) => {
    return dispatch(addPromotion(promotionData)).unwrap();
  }, [dispatch]);
  
  // Update an existing promotion
  const updatePromotionData = useCallback((id: string, data: Partial<BeltPromotion>) => {
    return dispatch(updatePromotion({ id, data })).unwrap();
  }, [dispatch]);
  
  // Delete a promotion
  const removePromotion = useCallback((id: string, userId: string) => {
    return dispatch(deletePromotion({ id, userId })).unwrap();
  }, [dispatch]);
  
  // Clear all promotions from state
  const clearAllPromotions = useCallback(() => {
    dispatch(clearPromotions());
  }, [dispatch]);
  
  // Clear any error
  const clearError = useCallback(() => {
    dispatch(resetError());
  }, [dispatch]);
  
  // Get promotions for a specific martial art
  const getPromotionsByMartialArt = useCallback((martialArt: string) => {
    return promotionsArray.filter(promotion => promotion.martialArt === martialArt);
  }, [promotionsArray]);
  
  // Calculate time between promotions
  const getTimeBetweenPromotions = useCallback((martialArt: string) => {
    const artPromotions = getPromotionsByMartialArt(martialArt)
      .sort((a, b) => a.date - b.date); // Sort by date, oldest first
    
    if (artPromotions.length < 2) {
      return null;
    }
    
    const timeBetween: { fromBelt: string; toBelt: string; days: number }[] = [];
    
    for (let i = 1; i < artPromotions.length; i++) {
      const prevPromotion = artPromotions[i - 1];
      const currPromotion = artPromotions[i];
      
      const daysBetween = Math.floor((currPromotion.date - prevPromotion.date) / (1000 * 60 * 60 * 24));
      
      timeBetween.push({
        fromBelt: `${prevPromotion.beltColor}${prevPromotion.stripes > 0 ? ` (${prevPromotion.stripes} stripes)` : ''}`,
        toBelt: `${currPromotion.beltColor}${currPromotion.stripes > 0 ? ` (${currPromotion.stripes} stripes)` : ''}`,
        days: daysBetween
      });
    }
    
    return timeBetween;
  }, [getPromotionsByMartialArt]);
  
  // Total training time
  const getTotalTrainingTime = useCallback((martialArt: string) => {
    const artPromotions = getPromotionsByMartialArt(martialArt)
      .sort((a, b) => a.date - b.date); // Sort by date, oldest first
    
    if (artPromotions.length === 0) {
      return 0;
    }
    
    const firstPromotionDate = artPromotions[0].date;
    const now = Date.now();
    const totalDays = Math.floor((now - firstPromotionDate) / (1000 * 60 * 60 * 24));
    
    return totalDays;
  }, [getPromotionsByMartialArt]);
  
  // Format a time period between dates for display
  const formatTimePeriod = useCallback((startDate: number | string | Date, endDate: number | string | Date): string => {
    // Convert to Date objects if they aren't already
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    
    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      logger.error(`Invalid date value in formatTimePeriod: start=${startDate}, end=${endDate}`);
      return 'Unknown time period';
    }
    
    // Calculate months difference
    const months = (end.getFullYear() - start.getFullYear()) * 12 +
                  (end.getMonth() - start.getMonth());
    
    // Convert to milliseconds for day calculation
    const startMs = start.getTime();
    const endMs = end.getTime();

    if (months < 1) {
      const days = Math.floor((endMs - startMs) / (1000 * 60 * 60 * 24));
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return remainingMonths > 0
        ? `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`
        : `${years} year${years !== 1 ? 's' : ''}`;
    }
  }, []);
  
  // Calculate average time between promotions
  const getAveragePromotionTime = useCallback((martialArt: string): string | null => {
    const timeBetween = getTimeBetweenPromotions(martialArt);
    
    if (!timeBetween || timeBetween.length === 0) {
      return null;
    }
    
    const totalDays = timeBetween.reduce((sum, period) => sum + period.days, 0);
    const avgDays = Math.round(totalDays / timeBetween.length);
    
    // Format the average time
    if (avgDays < 30) {
      return `${avgDays} day${avgDays !== 1 ? 's' : ''}`;
    } else if (avgDays < 365) {
      const months = Math.round(avgDays / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(avgDays / 365);
      const remainingDays = avgDays % 365;
      const months = Math.round(remainingDays / 30);
      
      return months > 0
        ? `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`
        : `${years} year${years !== 1 ? 's' : ''}`;
    }
  }, [getTimeBetweenPromotions]);
  
  return {
    // State
    promotions: promotionsArray,
    currentBelts,
    isLoading,
    error,
    
    // Actions
    loadUserPromotions,
    createPromotion,
    updatePromotionData,
    removePromotion,
    clearAllPromotions,
    clearError,
    
    // Helpers
    getCurrentBelt,
    getPromotionsByMartialArt,
    getTimeBetweenPromotions,
    getTotalTrainingTime,
    formatTimePeriod,
    getAveragePromotionTime
    // getPromotionHistory removed - use selectPromotionHistory directly in components
  };
};

export default useBeltProgression;